import { getApiEndpoint } from '../config/apiEndpoints';
import { fetchWithAuth } from '../utils/tokenRefresh';

const LINK_CHECKER_API_URL = getApiEndpoint('MAIN', '/link-checker-api'); // Using the generic endpoint for now

export interface AllowedFailure {
  url: string;
  status_code: number;
}

export interface LinkCheckerConfig {
  config_uuid: string;
  username: string;
  website_url: string;
  description: string;
  created_at: string;
  updated_at: string;
  allowed_failures?: AllowedFailure[];
}

export interface LinkCheckerRun {
  run_id: string;
  config_uuid: string;
  timestamp: string;
  description: string;
  status_result: 'IN_PROGRESS' | 'SUCCEEDED' | 'FAILED' | 'PASS' | 'FAIL';
  failures: string[];
}

interface ApiResponse<T> {
  message?: string;
  error?: string;
  config?: LinkCheckerConfig;
  configs?: LinkCheckerConfig[];
  run?: LinkCheckerRun;
  runs?: LinkCheckerRun[];
  [key: string]: any; // Allow other properties
}

const handleResponse = async <T,>(response: Response): Promise<ApiResponse<T>> => {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || data.message || 'Something went wrong');
  }
  return data;
};

// --- Configs API ---
export const createConfig = async (website_url: string, description: string): Promise<LinkCheckerConfig> => {
  const response = await fetchWithAuth(LINK_CHECKER_API_URL, {
    method: 'POST',
    body: JSON.stringify({
      request_type: 'create_config',
      website_url,
      description,
    }),
  });
  const data = await handleResponse<LinkCheckerConfig>(response);
  if (!data.config) throw new Error("Failed to create config: no config returned.");
  return data.config;
};

export const listConfigs = async (): Promise<LinkCheckerConfig[]> => {
  const response = await fetchWithAuth(LINK_CHECKER_API_URL, {
    method: 'POST',
    body: JSON.stringify({
      request_type: 'list_configs',
    }),
  });
  const data = await handleResponse<LinkCheckerConfig[]>(response);
  return data.configs || [];
};

export const getConfig = async (config_uuid: string): Promise<LinkCheckerConfig> => {
  const response = await fetchWithAuth(LINK_CHECKER_API_URL, {
    method: 'POST',
    body: JSON.stringify({
      request_type: 'get_config',
      config_uuid,
    }),
  });
  const data = await handleResponse<LinkCheckerConfig>(response);
  if (!data.config) throw new Error("Failed to get config: no config returned.");
  return data.config;
};

export const updateConfig = async (config_uuid: string, website_url: string, description: string): Promise<LinkCheckerConfig> => {
  const response = await fetchWithAuth(LINK_CHECKER_API_URL, {
    method: 'POST',
    body: JSON.stringify({
      request_type: 'update_config',
      config_uuid,
      website_url,
      description,
    }),
  });
  const data = await handleResponse<LinkCheckerConfig>(response);
  if (!data.config) throw new Error("Failed to update config: no config returned.");
  return data.config;
};

export const deleteConfig = async (config_uuid: string): Promise<void> => {
  const response = await fetchWithAuth(LINK_CHECKER_API_URL, {
    method: 'POST',
    body: JSON.stringify({
      request_type: 'delete_config',
      config_uuid,
    }),
  });
  await handleResponse(response);
};

// --- Allowed Failures API ---
export const updateAllowedFailures = async (config_uuid: string, allowed_failures: AllowedFailure[]): Promise<LinkCheckerConfig> => {
  const response = await fetchWithAuth(LINK_CHECKER_API_URL, {
    method: 'POST',
    body: JSON.stringify({
      request_type: 'update_allowed_failures',
      config_uuid,
      allowed_failures,
    }),
  });
  const data = await handleResponse<LinkCheckerConfig>(response);
  if (!data.config) throw new Error("Failed to update allowed failures: no config returned.");
  return data.config;
};

// --- Trigger API ---
export const triggerCheck = async (config_uuid: string): Promise<LinkCheckerRun> => {
  const response = await fetchWithAuth(LINK_CHECKER_API_URL, {
    method: 'POST',
    body: JSON.stringify({
      request_type: 'trigger_check',
      config_uuid,
    }),
  });
  const data = await handleResponse<LinkCheckerRun>(response);
  if (!data.run) throw new Error("Failed to trigger check: no run returned.");
  return data.run;
};

// --- Runs API ---
export const listRunsByConfig = async (config_uuid: string): Promise<LinkCheckerRun[]> => {
  const response = await fetchWithAuth(LINK_CHECKER_API_URL, {
    method: 'POST',
    body: JSON.stringify({
      request_type: 'list_runs',
      config_uuid,
    }),
  });
  const data = await handleResponse<LinkCheckerRun[]>(response);
  return data.runs || [];
};

export const getRun = async (run_id: string): Promise<LinkCheckerRun> => {
  const response = await fetchWithAuth(LINK_CHECKER_API_URL, {
    method: 'POST',
    body: JSON.stringify({
      request_type: 'get_run',
      run_id,
    }),
  });
  const data = await handleResponse<LinkCheckerRun>(response);
  if (!data.run) throw new Error("Failed to get run: no run returned.");
  return data.run;
};
